import { sum } from 'lodash'

import { GraphCompareResponse, GraphStackResponse, GraphStackWithAverageResponse } from 'src/api/gen/api'
import { exportDataAsCSV, formatDateExport, spreadBy } from 'src/commons/export/Export'
import { getTranslation } from 'src/commons/i18n'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import { GranularityEnum, IFeature } from 'src/commons/types'
import { getDocCountSum } from 'src/commons/utils'
import { getDataInPercentages } from 'src/pages/Dashboard/TripsCharts/charts'

// Graphs TripsDistribution
export function exportTripsDistribution(download = true) {
  const { t } = getTranslation()
  const translateCategory = (category: string) => t(enumsTranslationKeys.DeviceType[category])

  return function (filename: string, graphData: GraphStackWithAverageResponse) {
    const jsonList = graphData.data.map((bucket) => {
      return {
        [t('kpi.export.DistanceTraveled')]: bucket.key,
        ...spreadBy(bucket.stackedBy.buckets, undefined, true, translateCategory),
        [t('kpi.export.NumberOfTrips')]: getDocCountSum(bucket.stackedBy.buckets),
      }
    })

    if (download) {
      exportDataAsCSV(jsonList, filename)
    }
    return jsonList
  }
}

export function exportTripsDistributionComparing(download = true) {
  const { t } = getTranslation()
  const translateCategory = (category: string) => t(enumsTranslationKeys.DeviceType[category])

  return function (filename: string, graphData: GraphStackWithAverageResponse) {
    const total = sum(
      graphData.data.map((item) => {
        return getDocCountSum(item.stackedBy.buckets)
      })
    )
    const jsonList = graphData.data.map((bucket) => {
      const formattedBuckets = getDataInPercentages(bucket, total)
      return {
        [t('kpi.export.DistanceTraveled')]: bucket.key,
        ...spreadBy(formattedBuckets, '%', true, translateCategory),
        [t('kpi.export.PercentageOfTrips')]: Math.round((getDocCountSum(bucket.stackedBy.buckets) / total) * 100),
      }
    })

    if (download) {
      exportDataAsCSV(jsonList, filename)
    }
    return jsonList
  }
}

// Graphs TripsDuration
export function exportTripsDuration() {
  const { t } = getTranslation()
  const translateCategory = (category: string) => t(enumsTranslationKeys.DeviceType[category])

  return (bucket: GraphStackResponse): Record<string, unknown> => ({
    [t('kpi.export.DistanceTraveled')]: bucket.key,
    ...spreadBy(bucket.stackedBy.buckets, undefined, true, translateCategory),
    [t('kpi.export.NumberOfTrips')]: getDocCountSum(bucket.stackedBy.buckets),
  })
}

// Component TripsPerProviderComponent
export function formatExportTripsPerProvider(granularity: GranularityEnum) {
  const { t } = getTranslation()
  return (bucket: GraphStackResponse): Record<string, unknown> => ({
    [t('kpi.export.Date')]: formatDateExport(bucket.key, granularity),
    ...spreadBy(bucket.stackedBy.buckets, '(%)', true),
  })
}

// Graph TripsPerOriginZone / TripsPerDestinationZone
export function formatExportTripsPerZoneOrigin() {
  const { t } = getTranslation()
  return (feature: IFeature) => ({
    [t('kpi.export.OriginZone')]: feature.properties.label,
    [t('kpi.export.TotalTrips')]: feature.properties.value,
  })
}

export function formatExportTripsPerZoneDestination() {
  const { t } = getTranslation()
  return (feature: IFeature) => ({
    [t('kpi.export.DestinationZone')]: feature.properties.label,
    [t('kpi.export.TotalTrips')]: feature.properties.value,
  })
}

// Graph GazEmissionPerProviderComponent
export function formatExportClosePublicTransportStops(granularity: GranularityEnum) {
  const { t } = getTranslation()
  return (bucket: GraphCompareResponse) => ({
    [t('kpi.export.Date')]: formatDateExport(bucket.key, granularity),
    ...spreadBy(bucket.compareBy.buckets),
  })
}
